import type {ForwardedRef, MouseEvent, ReactElement} from 'react';
import React from 'react';
import {isValidURL} from '@Utils/url.util';
import {noop} from '@Utils/general.util';
import style from './text.module.scss';

export interface TextProps {
  val: string;
  size?: TextSize;
  type?: TextType;
  className?: string;
  bold?: boolean;
  underline?: boolean;
  href?: string;
  onClick?: (event: MouseEvent) => void;
  dangerouslySetInnerHTML?: boolean;
  /**
   * a key value pair of any attributes we want to give to the root component such as data-id etc
   */
  attributes?: Record<string, string>;
  disableSelection?: boolean;
  /**
   * Set this to enable click tracking for this text element on GA4.
   */
  ga4ClickName?: string;
  title?: string;
  /**
   * Render the text inside a semantic heading element (h1–h4) instead of the default span.
   * Ignored when a valid href makes it a link.
   */
  tag?: TextTag;
}

export enum TextTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
}

export enum TextSize {
  // New Design System sizes (use these for new code)
  LG = 'lg', // 16px
  MD = 'md', // 14px
  SM = 'sm', // 12px
  XS = 'xs', // 10px

  // Deprecated sizes — migrate to new DS values above:
  //   SMALL (16px)  → LG
  //   XSMALL (14px) → MD
  //   XXSMALL (12px)→ SM
  //   XXXSMALL (10px)→ XS
  //   MEDIUM (18px), LARGE (20px), XLARGE (24px) → no new equivalent
  XXXSMALL = 'xxxsmall',
  XXSMALL = 'xxsmall',
  XSMALL = 'xsmall',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XLARGE = 'xlarge',
}

export enum TextType {
  BODY = 'body',
  CAPTION = 'caption',
}

export const Text = React.forwardRef<HTMLAnchorElement | HTMLSpanElement | HTMLHeadingElement, TextProps>(
  (
    {bold = false, size = TextSize.MEDIUM, type = TextType.BODY, className = '', href = '', onClick = noop, attributes = {}, dangerouslySetInnerHTML = false, ...props},
    ref
  ): ReactElement<any> => {
    const getClassForSize = (): string => {
      // New Design System sizes — weight is part of the class name
      switch (size) {
        case TextSize.LG:
          return bold ? 'body-lg-bold' : 'body-lg-regular';
        case TextSize.MD:
          return bold ? 'body-md-bold' : 'body-md-regular';
        case TextSize.SM:
          return bold ? 'body-sm-bold' : 'body-sm-regular';
        case TextSize.XS:
          return bold ? 'body-xs-bold' : 'body-xs-regular';
      }

      // Deprecated sizes — mapped to new DS equivalents where available
      switch (size) {
        case TextSize.XXXSMALL:
          return bold ? 'body-xs-bold' : 'body-xs-regular';
        case TextSize.XXSMALL:
          return bold ? 'body-sm-bold' : 'body-sm-regular';
        case TextSize.XSMALL:
          return bold ? 'body-md-bold' : 'body-md-regular';
        case TextSize.SMALL:
          return bold ? 'body-lg-bold' : 'body-lg-regular';
      }

      // Deprecated sizes without new DS equivalent — uses old suffix concatenation pattern
      let textClassForSize: string = type;
      switch (size) {
        case TextSize.MEDIUM:
          textClassForSize += '-m';
          break;
        case TextSize.LARGE:
          textClassForSize += '-l';
          break;
        case TextSize.XLARGE:
          textClassForSize += '-xl';
          break;
        default:
          throw new Error(`Unknown type provided for Text size: ${size}`);
      }

      if (bold) {
        return `${textClassForSize}-bold`;
      }

      return textClassForSize;
    };

    const isLink = (): boolean => {
      return href !== '' && isValidURL(href);
    };

    const attachHrefIfApplicable = (): void => {
      if (isLink()) {
        attributes.href = href;
      }
    };

    const getClassesForStyles = (): string => {
      return `${props.underline ? style.underLine : ''} ${props.disableSelection ? style.disableSelection : ''}`;
    };

    const onElClick = (e: MouseEvent): void => {
      if (typeof window.PMW.gtm !== undefined && props.ga4ClickName !== undefined && props.ga4ClickName !== '') {
        window.PMW.gtm.trackGA4CustomClick(props.ga4ClickName);
      }
      onClick(e);
    };

    const getTextComponent = (): ReactElement<any> => {
      if (props.tag !== undefined && !isLink()) {
        const headingClassName = `${getClassForSize()} ${className} ${getClassesForStyles()}`;
        if (dangerouslySetInnerHTML) {
          return React.createElement(props.tag, {title: props.title, ref, onClick: onElClick, ...attributes, className: headingClassName, dangerouslySetInnerHTML: {__html: props.val}});
        }
        return React.createElement(props.tag, {title: props.title, ref, onClick: onElClick, ...attributes, className: headingClassName}, props.val);
      }

      if (dangerouslySetInnerHTML) {
        return isLink() ? (
          <a
            title={props.title}
            ref={ref as ForwardedRef<HTMLAnchorElement>}
            onClick={onElClick}
            {...attributes}
            className={`${getClassForSize()} ${className} ${getClassesForStyles()}`}
            dangerouslySetInnerHTML={{__html: props.val}}
          />
        ) : (
          <span
            title={props.title}
            ref={ref as ForwardedRef<HTMLSpanElement>}
            onClick={onElClick}
            {...attributes}
            className={`${getClassForSize()} ${className} ${getClassesForStyles()}`}
            dangerouslySetInnerHTML={{__html: props.val}}
          />
        );
      }
      return isLink() ? (
        <a
          title={props.title}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          onClick={onClick}
          {...attributes}
          className={`${getClassForSize()} ${className} ${getClassesForStyles()}`}
        >
          {props.val}
        </a>
      ) : (
        <span
          title={props.title}
          ref={ref as ForwardedRef<HTMLSpanElement>}
          onClick={onClick}
          {...attributes}
          className={`${getClassForSize()} ${className} ${getClassesForStyles()}`}
        >
          {props.val}
        </span>
      );
    };

    attachHrefIfApplicable();

    return getTextComponent();
  }
);
